/**
 * Plivo Browser SDK Service
 * Handles WebRTC click-to-call functionality
 *
 * Key fixes applied (SDK v2.2.x):
 * 1. Removed manual <audio> DOM element creation (remoteAudio/ringbackTone) - SDK v2 manages audio internally.
 * 2. Fixed speaker device setup: use enumerateDevices() AFTER getUserMedia(), not speakerDevices.get() which returns null.
 * 3. Added graceful fallback to default system audio when explicit device selection is not possible.
 * 4. Added enableQualityTracking option for SDK v2.
 */

import { identifyCountryCode } from '../utils/numberMasking';

class PlivoService {
    constructor() {
        this.plivoSDK = null;
        this.isLoggedIn = false;
        this.isInitialized = false;
        this.callState = 'idle'; // idle, connecting, ringing, answered, ended, failed
        this.currentCallNumber = null;
        this.onStateChange = null;
        this.stateListeners = []; // Additional listeners for multiple components
    }

    _normalizeDestinationNumber(destinationNumber) {
        if (!destinationNumber) return '';

        let cleanNumber = String(destinationNumber).trim().replace(/[\s-()]/g, '');
        if (!cleanNumber) return '';

        if (cleanNumber.startsWith('00')) {
            cleanNumber = '+' + cleanNumber.slice(2);
        }

        if (cleanNumber.startsWith('+')) {
            return cleanNumber;
        }

        const { code } = identifyCountryCode(cleanNumber);
        if (code) {
            return '+' + cleanNumber;
        }

        if (/^\d{10}$/.test(cleanNumber)) {
            return '+91' + cleanNumber;
        }

        if (/^91\d{10}$/.test(cleanNumber)) {
            return '+' + cleanNumber;
        }

        return cleanNumber;
    }

    /**
     * Initialize the Plivo Browser SDK
     * @param {Function} onStateChange - Callback when call state changes
     */
    init(onStateChange) {
        if (this.isInitialized) {
            console.log('Plivo: Already initialized');
            return false; // Return false so caller knows to use addStateListener instead
        }

        if (typeof window.Plivo === 'undefined') {
            console.error('Plivo SDK not loaded. Make sure the script is included in index.html');
            return false;
        }

        this.onStateChange = onStateChange;

        // NOTE: Do NOT manually create <audio id="remoteAudio"> or <audio id="ringbackTone"> elements.
        // SDK v2.x manages audio routing internally. Creating these manually causes the
        // "[WARN] No speaker element found" warning because the SDK ignores hand-created elements.

        const options = {
            debug: "INFO",
            permOnClick: true,
            codecs: ["OPUS", "PCMU"],
            enableIPV6: false,
            dscp: true,
            enableTracking: true,
            enableQualityTracking: "ALL", // Required for SDK v2.x
            useDefaultAudioDevice: true,
            audioConstraints: { optional: [{ googAutoGainControl: false }] },
        };

        this.plivoSDK = new window.Plivo(options);

        // -----------------------------------------------------------------------
        // onLogin: Request mic permission, then enumerate REAL output devices.
        // speakerDevices.get() returns null until enumerateDevices() is called
        // AFTER a getUserMedia() grant. This was the root cause of the
        // "[WARN] No speaker element found" warning.
        // -----------------------------------------------------------------------
        this.plivoSDK.client.on('onLogin', async () => {
            this.isLoggedIn = true;
            console.log('Plivo: Logged in successfully');

            try {
                console.log('Plivo: Requesting microphone permission...');
                // Must call getUserMedia first — this unlocks device label/ID enumeration in Chrome
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                console.log('Plivo: Microphone permission granted');
                // Stop immediately — we only needed the permission grant to unlock enumeration
                stream.getTracks().forEach(track => track.stop());

                // Now enumerate ALL devices — only returns full info AFTER getUserMedia grant
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
                console.log('Plivo: Available audio output devices:', audioOutputs.length);

                if (audioOutputs.length > 0) {
                    try {
                        await this.plivoSDK.client.audio.speakerDevices.set(audioOutputs[0].deviceId);
                        console.log('Plivo: Speaker set to:', audioOutputs[0].label || audioOutputs[0].deviceId);
                    } catch (setSpeakerErr) {
                        // Non-fatal: SDK will fall back to system default
                        console.warn('Plivo: Could not set speaker device explicitly, using system default:', setSpeakerErr.message);
                    }

                    try {
                        await this.plivoSDK.client.audio.ringtoneDevices.set(audioOutputs[0].deviceId);
                        console.log('Plivo: Ringtone device set to:', audioOutputs[0].label || audioOutputs[0].deviceId);
                    } catch (setRingtoneErr) {
                        console.warn('Plivo: Could not set ringtone device explicitly:', setRingtoneErr.message);
                    }
                } else {
                    // Browser does not expose output devices (common in some Chrome contexts).
                    // The SDK will use the system default — this is fine and non-fatal.
                    console.log('Plivo: No enumerable audio output devices. Using system default speaker.');
                }
            } catch (audioError) {
                // Microphone permission denied or device error — log and continue.
                // Calls may still work with system defaults.
                console.error('Plivo: Error during audio device setup:', audioError.message);
            }

            this._updateState('idle');
        });

        this.plivoSDK.client.on('onLoginFailed', (error) => {
            this.isLoggedIn = false;
            console.error('Plivo: Login failed', error);
            this._updateState('error', 'Login failed');
        });

        this.plivoSDK.client.on('onCalling', () => {
            console.log('Plivo: Call connecting...');
            this._updateState('connecting');
        });

        this.plivoSDK.client.on('onCallRemoteRinging', () => {
            console.log('Plivo: Call ringing...');
            this._updateState('ringing');
        });

        this.plivoSDK.client.on('onCallAnswered', () => {
            console.log('Plivo: Call answered');
            this._updateState('answered');
        });

        this.plivoSDK.client.on('onCallFailed', (error) => {
            console.error('Plivo: Call failed', error);
            this._updateState('failed', error);
            this.currentCallNumber = null;
            setTimeout(() => { this._updateState('idle'); }, 1500);
        });

        this.plivoSDK.client.on('onCallTerminated', () => {
            console.log('Plivo: Call ended');
            this._updateState('ended');
            this.currentCallNumber = null;
            setTimeout(() => { this._updateState('idle'); }, 1500);
        });

        this.plivoSDK.client.on('onCallRejected', () => {
            console.log('Plivo: Call rejected by callee');
            this._updateState('failed', 'Call rejected');
            this.currentCallNumber = null;
            setTimeout(() => { this._updateState('idle'); }, 1500);
        });

        this.plivoSDK.client.on('onCallUnavailable', () => {
            console.log('Plivo: Callee unavailable');
            this._updateState('failed', 'Unavailable');
            this.currentCallNumber = null;
            setTimeout(() => { this._updateState('idle'); }, 1500);
        });

        this.plivoSDK.client.on('onMediaPermission', (permissionState) => {
            console.log('Plivo: Media permission', permissionState);
        });

        this.isInitialized = true;
        console.log('Plivo: SDK initialized');
        return true;
    }

    /**
     * Login to Plivo with endpoint credentials
     */
    login(endpoint, password) {
        if (!this.plivoSDK) {
            console.error('Plivo: SDK not initialized');
            return false;
        }

        console.log('Plivo: Logging in with endpoint:', endpoint);
        this.plivoSDK.client.login(endpoint, password);
        return true;
    }

    /**
     * Make an outbound call
     * @param {string} destinationNumber - Phone number to call (with country code)
     * @param {string} callerId - Optional caller ID / client UUID passed as extra header
     */
    call(destinationNumber, callerId = null) {
        if (!this.isLoggedIn) {
            console.error('Plivo: Not logged in');
            return false;
        }

        if (this.callState !== 'idle' && this.callState !== 'ended') {
            console.error('Plivo: Already in a call');
            return false;
        }

        // Normalize the number for outbound dialing
        const cleanNumber = this._normalizeDestinationNumber(destinationNumber);
        if (!cleanNumber) {
            console.error('Plivo: Invalid destination number');
            return false;
        }

        console.log('Plivo: Calling', cleanNumber);
        this.currentCallNumber = cleanNumber;

        try {
            const customCallerId = callerId || process.env.REACT_APP_PLIVO_CALLER_ID;
            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(customCallerId);

            if (isUuid) {
                console.log('🆔 Using Resolution ID for lookup:', customCallerId);
            } else {
                console.log('📞 Using Plivo number:', customCallerId);
            }

            const extraHeaders = {
                'X-PH-Test1': 'test1',
                'X-PH-header1': customCallerId
            };

            this.plivoSDK.client.call(cleanNumber, extraHeaders);
            return true;
        } catch (error) {
            console.error('Plivo: Call error', error);
            this._updateState('failed', error.message);
            return false;
        }
    }

    /**
     * Hangup the current call
     */
    hangup() {
        if (!this.plivoSDK) return false;
        console.log('Plivo: Hanging up');
        this.plivoSDK.client.hangup();
        return true;
    }

    /**
     * Mute/unmute the call
     */
    setMute(mute) {
        if (!this.plivoSDK) return false;
        if (mute) {
            this.plivoSDK.client.mute();
        } else {
            this.plivoSDK.client.unmute();
        }
        return true;
    }

    /**
     * Logout from Plivo
     */
    logout() {
        if (!this.plivoSDK) return;
        this.plivoSDK.client.logout();
        this.isLoggedIn = false;
        this._updateState('idle');
    }

    /**
     * Get current call state
     */
    getCallState() {
        return {
            state: this.callState,
            isLoggedIn: this.isLoggedIn,
            currentNumber: this.currentCallNumber
        };
    }

    /**
     * Add an additional state listener (for components that mount after init)
     */
    addStateListener(listener) {
        if (listener && !this.stateListeners.includes(listener)) {
            this.stateListeners.push(listener);
        }
    }

    /**
     * Remove a state listener
     */
    removeStateListener(listener) {
        this.stateListeners = this.stateListeners.filter(l => l !== listener);
    }

    /**
     * Update call state and notify all listeners
     * @private
     */
    _updateState(state, error = null) {
        this.callState = state;
        const stateObj = {
            state,
            error,
            currentNumber: this.currentCallNumber,
            isLoggedIn: this.isLoggedIn
        };
        // Notify global listeners for UI updates, such as refreshing leads after a call completes
        if (typeof window !== 'undefined' && ['ended', 'failed'].includes(state)) {
            window.dispatchEvent(new Event('leadsUpdated'));
        }

        // Notify primary listener
        if (this.onStateChange) {
            this.onStateChange(stateObj);
        }
        // Notify additional listeners
        this.stateListeners.forEach(listener => listener(stateObj));
    }
}

// Export singleton instance
const plivoService = new PlivoService();
export default plivoService;